import { z } from 'zod';
export const TimeEntryStatusSchema = z.enum(['UNBILLED', 'BILLED', 'NON_BILLABLE']);
export const TimeEntryStatusLabel = {
    UNBILLED: 'Sin facturar',
    BILLED: 'Facturado',
    NON_BILLABLE: 'No facturable',
};
/** Fase del trabajo: PREVENTA (adquisición / CAP) o EJECUCION (entrega). */
export const TimeEntryPhaseSchema = z.enum(['PREVENTA', 'EJECUCION']);
export const TimeEntryPhaseLabel = {
    PREVENTA: 'Preventa',
    EJECUCION: 'Ejecución',
};
export const TimeEntryDtoSchema = z.object({
    id: z.string(),
    opportunityId: z.string(),
    opportunityCode: z.string(),
    opportunityTitle: z.string(),
    clientId: z.string(),
    clientDisplayName: z.string(),
    userId: z.string(),
    userName: z.string(),
    taskId: z.string().nullable(),
    taskTitle: z.string().nullable(),
    description: z.string(),
    performedOn: z.string().datetime(),
    durationMinutes: z.number().int(),
    startedAt: z.string().datetime().nullable(),
    endedAt: z.string().datetime().nullable(),
    hourlyRateCents: z.number().int(),
    amountCents: z.number().int(),
    /** Coste interno por hora (céntimos/hora). Campo interno (margen). */
    costRateCents: z.number().int().min(0).nullable().optional(),
    /** F34: rol de proyecto bajo el que se imputa. null para entries pre-F34. */
    projectRoleId: z.string().nullable().optional(),
    projectRoleName: z.string().nullable().optional(),
    status: TimeEntryStatusSchema,
    phase: TimeEntryPhaseSchema.default('EJECUCION'),
    invoiceLineId: z.string().nullable(),
    createdAt: z.string().datetime(),
});
export const CreateTimeEntrySchema = z.object({
    opportunityId: z.string(),
    /**
     * F45 — Persona a la que se imputa el tiempo. Opcional: por defecto el usuario
     * logueado. Solo un ADMIN puede fijarla distinta de sí mismo (imputar por otros);
     * el backend rechaza con 403 a un no-admin que intente imputar a un tercero.
     */
    userId: z.string().optional(),
    taskId: z.string().nullable().optional(),
    description: z.string().min(1).max(1000),
    performedOn: z.string().refine((v) => !Number.isNaN(Date.parse(v)), 'Fecha no válida'),
    /** Duración en minutos. Si se usó cronómetro, se calcula client-side desde start/end. */
    durationMinutes: z.coerce.number().int().min(1).max(24 * 60),
    startedAt: z.string().nullable().optional(),
    endedAt: z.string().nullable().optional(),
    /**
     * F34: rol de proyecto bajo el que se imputa el trabajo. Opcional: si el
     * usuario tiene una sola asignación en la oportunidad, el backend lo
     * autoasigna. Si tiene varias y este campo es null/undefined, el backend
     * responde 400 con la lista de roles candidatos en `choices`.
     */
    projectRoleId: z.string().nullable().optional(),
    /** Override de tarifa (en céntimos/hora). Si null, se calcula del usuario/firm. */
    hourlyRateCents: z.coerce.number().int().min(0).optional(),
    /** Coste interno por hora (céntimos/hora). Campo interno (margen). */
    costRateCents: z.coerce.number().int().min(0).nullable().optional(),
    /** Forzar status NON_BILLABLE al crear (ej. interno). */
    nonBillable: z.boolean().default(false),
    phase: TimeEntryPhaseSchema.default('EJECUCION'),
});
export const UpdateTimeEntrySchema = z.object({
    description: z.string().min(1).max(1000).optional(),
    performedOn: z
        .string()
        .refine((v) => !Number.isNaN(Date.parse(v)), 'Fecha no válida')
        .optional(),
    durationMinutes: z.coerce.number().int().min(1).max(24 * 60).optional(),
    /**
     * F34: si cambia el rol, el backend recalcula tarifa+coste desde el
     * snapshot del nuevo rol (a menos que también vengan en el body).
     */
    projectRoleId: z.string().nullable().optional(),
    hourlyRateCents: z.coerce.number().int().min(0).optional(),
    /** Coste interno por hora (céntimos/hora). Campo interno (margen). */
    costRateCents: z.coerce.number().int().min(0).nullable().optional(),
    status: TimeEntryStatusSchema.optional(),
    /** Atajo de facturabilidad (como en el POST): true → NON_BILLABLE, false → UNBILLED. */
    nonBillable: z.boolean().optional(),
    phase: TimeEntryPhaseSchema.default('EJECUCION'),
});
export const TimeEntryListQuerySchema = z.object({
    opportunityId: z.string().optional(),
    clientId: z.string().optional(),
    userId: z.string().optional(),
    status: TimeEntryStatusSchema.optional(),
    mine: z
        .union([z.boolean(), z.enum(['true', 'false'])])
        .transform((v) => v === true || v === 'true')
        .optional(),
    from: z.string().optional(),
    to: z.string().optional(),
});
export const TimeEntryListResponseSchema = z.object({
    items: z.array(TimeEntryDtoSchema),
    /** Totales agregados en BD sobre TODAS las filas del filtro (no solo las devueltas). */
    totalMinutes: z.number().int(),
    totalAmountCents: z.number().int(),
    /** Coste real interno agregado (Σ durationMinutes/60 × costRateCents) sobre todo el filtro. */
    totalCostCents: z.number().int().default(0),
    /** Nº total de filas que cumplen el filtro; si supera items.length, la lista está truncada. */
    totalCount: z.number().int().default(0),
});
export const ImportTimeEntriesSchema = z.object({
    timeEntryIds: z.array(z.string()).min(1).max(200),
    /** Si true, agrupa todas las entradas de la misma oportunidad en una sola línea. */
    groupByOpportunity: z.boolean().default(false),
});
//# sourceMappingURL=time-entries.js.map